import { useMemo, useState } from "react";

const GROUP_ORDER = [
  "Roman Catholic", "Episcopal / Anglican", "Lutheran", "Methodist",
  "Presbyterian", "Baptist / Free Church", "General / Ecumenical",
];

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function matches(r, q) {
  if (!q) return true;
  const hay = [
    r.name, r.publisher, r.resource_type,
    ...(r.denominations || []), ...(r.traditions || []),
    ...(r.cultural_heritage || []), ...(r.languages || []),
  ].join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function LiturgicalLibrarySection({ catalog, wi, update }) {
  const [query, setQuery] = useState("");
  const tradition = wi.liturgical_tradition || "";
  const selected = wi.liturgical_library || [];
  const usage = wi.liturgical_library_usage || {};
  const priority = wi.liturgical_library_priority || {};
  const required = wi.required_liturgical_resources || [];
  const custom = wi.custom_liturgical_resources || [];
  const usageLevels = catalog.usage_levels || [];
  const priorityLevels = catalog.priority_levels || [];

  // Group resources by denomination bucket → subgroup, with the parish's tradition first.
  const groups = useMemo(() => {
    const all = catalog.resources || [];
    const filtered = all.filter((r) => matches(r, query));
    const byGroup = {};
    for (const r of filtered) {
      const g = r.group || "General / Ecumenical";
      const sg = r.subgroup || "Other Resources";
      byGroup[g] = byGroup[g] || {};
      byGroup[g][sg] = byGroup[g][sg] || [];
      byGroup[g][sg].push(r);
    }
    const order = [...GROUP_ORDER];
    if (tradition && order.includes(tradition)) {
      order.splice(order.indexOf(tradition), 1);
      order.unshift(tradition);
    }
    return order.filter((g) => byGroup[g]).map((g) => ({ group: g, subgroups: byGroup[g] }));
  }, [catalog.resources, query, tradition]);

  const toggle = (name) => update("liturgical_library", (prev) => {
    const a = prev || [];
    return a.includes(name) ? a.filter((x) => x !== name) : [...a, name];
  });
  const setUsage = (name, level) => update("liturgical_library_usage", (prev) => ({ ...(prev || {}), [name]: level }));
  const setPriority = (name, level) => update("liturgical_library_priority", (prev) => ({ ...(prev || {}), [name]: level }));
  const toggleRequired = (name) => update("required_liturgical_resources", (prev) => {
    const a = prev || [];
    return a.includes(name) ? a.filter((x) => x !== name) : [...a, name];
  });

  const setCustom = (idx, key, val) => update("custom_liturgical_resources", (prev) => {
    const list = [...(prev || [])];
    list[idx] = { ...list[idx], [key]: val };
    return list;
  });
  const addCustom = () => update("custom_liturgical_resources", (prev) => [...(prev || []), {}]);
  const removeCustom = (idx) => update("custom_liturgical_resources", (prev) => (prev || []).filter((_, i) => i !== idx));

  return (
    <div className="pp-lib" data-testid="liturgical-library-section">
      <div className="pp-lib-head">
        <h4>Liturgical Library™ — Worship Resources</h4>
        <p>Select the hymnals, missals and resources your parish actually uses. Selections drive Ordo AI™ music recommendations and are cited by name.</p>
      </div>

      <input
        className="pp-lib-search"
        placeholder="Search your worship resources…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="library-search"
      />

      {groups.length === 0 && <div className="pp-lib-empty" data-testid="library-no-results">No resources match “{query}”.</div>}

      {groups.map(({ group, subgroups }) => (
        <div className="pp-lib-group" key={group} data-testid={`library-group-${slug(group)}`}>
          <div className="pp-lib-group-h">{group}{group === tradition ? " · Your tradition" : ""}</div>
          {Object.keys(subgroups).map((sg) => (
            <div className="pp-lib-sub" key={sg}>
              <div className="pp-lib-sub-h">{sg}</div>
              <div className="pp-multi">
                {subgroups[sg].map((r) => {
                  const on = selected.includes(r.name);
                  return (
                    <button type="button" key={r.id} className={`pp-chip${on ? " on" : ""}`}
                      data-testid={`library-chip-${r.id}`} onClick={() => toggle(r.name)}
                      title={`${r.publisher} · ${r.resource_type}`}>
                      {r.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}

      {selected.length > 0 && (
        <div className="pp-lib-selected" data-testid="library-selected">
          <div className="pp-lib-active-head">
            <h5>Active Liturgical Library™</h5>
            <p>Identify the resources your congregation actually uses so Ordo AI™ recommends music from the correct books.</p>
          </div>
          {selected.map((name) => {
            const req = required.includes(name);
            return (
              <div className="pp-lib-active-row" key={name} data-testid={`library-active-${slug(name)}`}>
                <span className="pp-lib-usage-name">{name}</span>
                <div className="pp-lib-active-controls">
                  <select value={usage[name] || ""} onChange={(e) => setUsage(name, e.target.value)}
                    data-testid={`library-usage-select-${slug(name)}`}>
                    <option value="">How often is this resource used?</option>
                    {usageLevels.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <select value={priority[name] || ""} onChange={(e) => setPriority(name, e.target.value)}
                    data-testid={`library-priority-select-${slug(name)}`}>
                    <option value="">Priority…</option>
                    {priorityLevels.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button type="button" className={`pp-req-toggle${req ? " on" : ""}`}
                    onClick={() => toggleRequired(name)} data-testid={`library-required-${slug(name)}`}>
                    {req ? "✓ Required Resource" : "Mark Required"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="pp-lib-custom" data-testid="custom-resources">
        <div className="pp-lib-sub-h">Parish Resource Collection™</div>
        <p className="pp-lib-note">Add resources unique to your parish, such as a parish festival hymnal, choir library, cathedral anthem collection, seasonal music binder, children's choir collection, or locally composed Mass setting.</p>
        {custom.map((c, idx) => (
          <div className="pp-lib-custom-item" key={idx} data-testid={`custom-resource-${idx}`}>
            <input placeholder="Resource Name" value={c.name || ""} onChange={(e) => setCustom(idx, "name", e.target.value)} data-testid={`custom-name-${idx}`} />
            <input placeholder="Publisher / Source" value={c.publisher || ""} onChange={(e) => setCustom(idx, "publisher", e.target.value)} data-testid={`custom-publisher-${idx}`} />
            <input placeholder="Resource Type" value={c.resource_type || ""} onChange={(e) => setCustom(idx, "resource_type", e.target.value)} data-testid={`custom-type-${idx}`} />
            <select value={c.usage || ""} onChange={(e) => setCustom(idx, "usage", e.target.value)} data-testid={`custom-usage-${idx}`}>
              <option value="">How often is this resource used?</option>
              {usageLevels.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <input placeholder="Notes" value={c.notes || ""} onChange={(e) => setCustom(idx, "notes", e.target.value)} data-testid={`custom-notes-${idx}`} />
            <button type="button" className="pp-remove" onClick={() => removeCustom(idx)} data-testid={`custom-remove-${idx}`}>Remove</button>
          </div>
        ))}
        <button type="button" className="pp-add" onClick={addCustom} data-testid="add-custom-resource">+ Add Parish Resource</button>
      </div>

      <div className="pp-lib-partner" data-testid="primary-resource-partner">
        <label>Primary Liturgical Publisher</label>
        <p className="pp-lib-note">Select the publisher or worship-resource publisher your congregation relies on most. Leave blank if your parish intentionally uses mixed resources.</p>
        <select value={wi.preferred_publisher || ""} onChange={(e) => update("preferred_publisher", e.target.value)}
          data-testid="pf-preferred_publisher">
          <option value="">—</option>
          {(catalog.publishers || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
    </div>
  );
}
